import React, { useState, useEffect } from 'react';
import Dropdown from './controls/Dropdown';
import { debug } from '../../helpers/debug';

const DocumentOptions = ({ editor, docId, editorRef }) => {
  const [currentLayout, setCurrentLayout] = useState('pageless');
  const [currentUnits, setCurrentUnits] = useState('pixels');

  const pageLayoutOptions = [
    { value: 'pageless', label: 'Pageless' },
    { value: 'letter', label: 'Letter' },
    { value: 'a4', label: 'A4' }
  ];

  const unitsOptions = [
    { value: 'pixels', label: 'Pixels' },
    { value: 'points', label: 'Points' }
  ];

  // Get current page layout from the editor's root element classes
  const getCurrentPageLayout = () => {
    if (!editor || !docId) return 'pageless';
    try {
      const outer = document.getElementById(`ghost-${docId}`);
      if (!outer) return 'pageless';
      if (outer.classList.contains('a4')) return 'a4';
      if (outer.classList.contains('letter')) return 'letter';
      if (outer.classList.contains('pageless')) return 'pageless';
      return 'pageless';
    } catch (error) {
      return 'pageless';
    }
  };

  // Get current units from the document element classes
  const getCurrentUnits = () => {
    if (!editor || !docId) return 'pixels';
    try {
      const outer = document.getElementById(`ghost-${docId}`);
      if (!outer) return 'pixels';
      if (outer.classList.contains('points')) return 'points';
      return 'pixels';
    } catch (error) {
      return 'pixels';
    }
  };

  // Update current layout and units when editor changes or docId changes
  useEffect(() => {
    if (!editor) {
      setCurrentLayout('pageless');
      setCurrentUnits('pixels');
      return;
    }
    const timeoutId = setTimeout(() => {
      try {
        if (!editor.view) {
          setCurrentLayout('pageless');
          setCurrentUnits('pixels');
          return;
        }
        const layout = getCurrentPageLayout();
        setCurrentLayout(layout);
        const units = getCurrentUnits();
        setCurrentUnits(units);
      } catch (error) {
        setCurrentLayout('pageless');
        setCurrentUnits('pixels');
      }
    }, 50);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docId]);

  const handlePageLayoutChange = (value) => {
    if (!editor || !docId) return;
    
    debug('handlePageLayoutChange', { type: 'audit', level: 3 }, { docId, layout: value });
    
    try {
      // Apply the page layout class to the outer `.editor` wrapper
      const outer = document.getElementById(`ghost-${docId}`);
      if (!outer) return;
      outer.classList.remove('letter', 'a4', 'pageless');
      outer.classList.add(value);
      
      // Update local state
      setCurrentLayout(value);
      
      // Save to database via debounced mechanism if we have docId and editorRef
      if (docId && editorRef?.current?.updateMetadata) {
        debug('updateMetadata', { type: 'audit', level: 3 }, { docId, layout: value });
        editorRef.current.updateMetadata(docId, { layout: value });
      } else {
        debug('DocumentOptions: Cannot save - missing docId or editorRef:', { type: 'warn' }, { docId, hasEditorRef: !!editorRef?.current });
      }
    } catch (error) {
      // Silently handle errors
      debug('Error setting page layout:', { type: 'warn' }, error);
    }
  };

  const handleUnitsChange = (value) => {
    if (!editor || !docId) return;
    
    try {
      const outer = document.getElementById(`ghost-${docId}`);
      if (!outer) return;
      outer.classList.remove('pixels', 'points');
      outer.classList.add(value);
      
      // Update local state
      setCurrentUnits(value);
    } catch (error) {
      debug('Error setting units:', { type: 'warn' }, error);
    }
  };

  return (
    <div className="documentOptions">
      <h3>Document</h3>
      <Dropdown
        label="Page Layout"
        value={currentLayout}
        options={pageLayoutOptions}
        onChange={handlePageLayoutChange}
      />
      <Dropdown
        label="Units"
        value={currentUnits}
        options={unitsOptions}
        onChange={handleUnitsChange}
      />
      {/* Shrink-to-fit is now toggled from the tab bar. */}
    </div>
  );
};

export default DocumentOptions;
